import { motion } from "framer-motion";
import { ArrowUpRight, Link as LinkIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { Navigation } from "./Navigation";
import { Footer } from "./Footer";
import { SectionHeading } from "./SectionHeading";
import { CtaButton } from "./CtaButton";
import { Reveal, EASE } from "./motion-primitives";
import { useSeo } from "./useSeo";

// Discoverography — demo destination/editorial content hidden (10A.1).
// Only founder-approved content (Da Nang After Dark) is shown publicly; page shell + design preserved.
export const DiscoverographyPage = () => {
  useSeo({
    title: "Discoverography — Travel Starts With a Feeling | Holideviser",
    description:
      "Editorial travel stories and collections from Holideviser — personally experienced, not crowdsourced.",
  });

  return (
    <div className="min-h-screen bg-ink">
      <Navigation />

      {/* Hero */}
      <section
        id="discoverography"
        className="mx-auto max-w-[1400px] px-6 pt-40 sm:px-10 lg:px-16 lg:pt-52"
      >
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE }}
        >
          <div className="mb-6 flex items-center gap-4">
            <span className="h-px w-10 bg-teal" />
            <span className="font-sans text-xs uppercase tracking-[0.3em] text-teal">
              Discoverography
            </span>
          </div>
          <h1
            data-testid="disc-hero-headline"
            className="max-w-4xl font-serif text-5xl font-light leading-[1.02] tracking-tight text-cream sm:text-6xl lg:text-7xl"
          >
            Travel starts with a <span className="italic text-teal">feeling.</span>
          </h1>
          <p className="mt-8 max-w-xl font-sans text-base leading-relaxed text-sand sm:text-lg">
            Travel stories and collections for people who'd rather know where to go than everywhere to go.
            No endless lists — just places we'd actually send you.
          </p>
        </motion.div>
      </section>

      {/* Verified collection — Da Nang After Dark (Knowledge Library) */}
      <section className="mx-auto max-w-[1400px] px-6 py-24 sm:px-10 lg:px-16 lg:py-32" data-testid="verified-collection-section">
        <SectionHeading
          overline="Personally verified · Da Nang"
          title="Collections we've actually stood in."
          intro="Editorial collections built from places Holideviser has personally experienced — not crowdsourced. More are on the way."
          testId="verified-collection-heading"
        />
        <Reveal>
          <Link
            to="/discoverography/da-nang-after-dark"
            data-testid="da-nang-after-dark-card"
            className="group relative mt-14 block overflow-hidden border border-charcoal lg:mt-20"
          >
            <div className="relative aspect-[16/10] w-full overflow-hidden sm:aspect-[21/9]">
              <img
                src="https://images.unsplash.com/photo-1500217052183-bc01eee1a74e?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
                alt="A single cocktail lit in a dark bar"
                className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10">
                <span className="font-sans text-[0.65rem] uppercase tracking-[0.28em] text-teal">
                  Da Nang · 2 places
                </span>
                <h3 className="mt-3 max-w-xl font-serif text-4xl font-light leading-tight text-cream sm:text-5xl">
                  Da Nang After Dark
                </h3>
                <p className="mt-3 max-w-md font-sans text-base text-sand">
                  Two hidden cocktail bars worth finding.
                </p>
                <span className="mt-6 inline-flex items-center gap-2 border-b border-teal pb-1 font-sans text-xs uppercase tracking-[0.18em] text-cream transition-colors group-hover:text-teal">
                  Open the collection
                  <ArrowUpRight size={15} strokeWidth={1.6} className="text-teal" />
                </span>
              </div>
            </div>
          </Link>
        </Reveal>

        {/* Bridge to the growing destination edits */}
        <Reveal delay={0.1}>
          <Link
            to="/destinations"
            data-testid="disc-destinations-link"
            className="mt-10 inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.18em] text-sand transition-colors hover:text-teal"
          >
            <LinkIcon size={14} strokeWidth={1.6} />
            Browse our destination edits
          </Link>
        </Reveal>
      </section>

      {/* Not sure where? */}
      <section className="bg-teal text-ink" data-testid="not-sure-section">
        <div className="mx-auto max-w-[1400px] px-6 py-24 sm:px-10 lg:px-16 lg:py-32">
          <Reveal>
            <span className="font-sans text-xs uppercase tracking-[0.3em] text-ink/60">
              Still deciding
            </span>
          </Reveal>
          <Reveal delay={0.06}>
            <h2 className="mt-8 max-w-3xl font-serif text-5xl font-light leading-[1.02] tracking-tight text-ink sm:text-6xl lg:text-7xl">
              Not sure where to go?
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-8 max-w-xl font-sans text-lg leading-relaxed text-ink/80">
              Tell us what you're looking for. We'll help you find the place.
            </p>
          </Reveal>
          <Reveal delay={0.18}>
            <div className="mt-12">
              <CtaButton testId="not-sure-plan-cta" variant="onTeal" to="/plan-my-trip">
                Plan My Trip
              </CtaButton>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};
